import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Modal, ModalContent } from '@finn-auto/design-system/atoms/modal';
import { RescheduleDialog } from './RescheduleDialog';
import type { PendingAction } from './usePendingActions';
import type { PipelineRow } from './pipelineRows';

/**
 * One Reschedule dialog for the whole board, the same shape as the notes and email-history
 * modals: the "in flight" strip sits on every tab, so the provider owns the dialog, the board
 * owner, the countdown and the refresh, and a row only asks to open it.
 */
interface RescheduleApi {
  openReschedule: (row: PipelineRow, postingId: string) => void;
}

const RescheduleCtx = createContext<RescheduleApi | null>(null);

/** Null outside a provider — the row then renders no button rather than a dead one. */
export function useReschedule(): RescheduleApi | null {
  return useContext(RescheduleCtx);
}

export function RescheduleProvider({
  boardEmail,
  onDefer,
  onChanged,
  onOpenChange,
  surface = 'board',
  children,
}: {
  /** The Log mounts this provider too; the surface keeps its analytics apart from the board's. */
  surface?: 'board' | 'log';
  boardEmail: string;
  onDefer: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  onChanged: () => void;
  /**
   * The board holds a stale-version reload until no modal is open. This dialog is one, and its
   * draft would be lost if the board reloaded underneath it (CodeRabbit, PR #643).
   */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [target, setTarget] = useState<{ row: PipelineRow; postingId: string } | null>(null);
  const openReschedule = useCallback(
    (row: PipelineRow, postingId: string) => {
      setTarget({ row, postingId });
      onOpenChange?.(true);
    },
    [onOpenChange]
  );
  const close = useCallback(() => {
    setTarget(null);
    onOpenChange?.(false);
  }, [onOpenChange]);
  // Unmounted with the dialog open (the board reloads, the recruiter switches boards): say so, or
  // the board would wait for a dialog that no longer exists and never take a new version.
  useEffect(() => () => onOpenChange?.(false), [onOpenChange]);
  return (
    <RescheduleCtx.Provider value={{ openReschedule }}>
      {children}
      {target && (
        <Modal open onOpenChange={(open: boolean) => !open && close()}>
          <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,40rem)] gap-5 overflow-auto rounded p-6">
            <RescheduleDialog
              row={target.row}
              postingId={target.postingId}
              boardEmail={boardEmail}
              onClose={close}
              onChanged={onChanged}
              onDefer={onDefer}
              surface={surface}
            />
          </ModalContent>
        </Modal>
      )}
    </RescheduleCtx.Provider>
  );
}
